import {
  acceptsType,
  together,
  UPLOAD_MAX_BYTES,
  UPLOAD_TYPES,
  type AnswerSuggestion,
  type DocumentSummary,
} from '@cyberscore/contracts';

/**
 * What the upload and review screens show (task 40), kept apart from the components so it can be tested.
 * The core decides for real: these checks only save a person an upload that would be refused.
 */

const BY_EXT = new Map<string, string>(
  Object.entries(UPLOAD_TYPES).flatMap(([type, t]) => t.ext.map((e) => [e, type] as const)),
);

/** The type the platform would read this file as, from its name (the core checks its bytes). */
export function guessType(name: string): string | null {
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase();
  return (ext && BY_EXT.get(ext)) ?? null;
}

/** For a file input's `accept`: the extensions a question takes (all the platform takes by default). */
export function acceptAttr(accept: readonly string[] = ['*/*']): string {
  return Object.entries(UPLOAD_TYPES)
    .filter(([type]) => accept.includes('*/*') || acceptsType(accept, type))
    .flatMap(([, t]) => t.ext.map((e) => `.${e}`))
    .join(',');
}

/** Plain words for what a question takes, e.g. "PDF, PNG image or JPEG image". */
export function acceptedKinds(accept: readonly string[] = ['*/*']): string {
  const labels = Object.entries(UPLOAD_TYPES)
    .filter(([type]) => accept.includes('*/*') || acceptsType(accept, type))
    .map(([, t]) => t.label);
  return labels.length > 1
    ? `${labels.slice(0, -1).join(', ')} or ${labels.at(-1)}`
    : (labels[0] ?? 'nothing');
}

/** Why a file can't be uploaded here, before sending it; null if it may be. */
export function fileProblem(
  file: { name: string; size: number },
  accept: readonly string[] = ['*/*'],
): string | null {
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > UPLOAD_MAX_BYTES)
    return `${file.name} is ${megabytes(file.size)}; files can be up to 25 MB.`;
  const type = guessType(file.name);
  if (!type) return `${file.name} isn’t a type we can read. Upload a ${acceptedKinds()}.`;
  if (!accept.includes('*/*') && !acceptsType(accept, type))
    return `This question takes a ${acceptedKinds(accept)}.`;
  return null;
}

export function megabytes(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** "insurance_schedule" → "Insurance schedule". */
export function typeLabel(key: string): string {
  const s = key.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export type Tone = 'ok' | 'bad' | 'neutral' | 'warn';

/** How long a document may wait for the background readers before the screen stops expecting them. */
const READING_MS = 5 * 60_000;

/**
 * Where a document stands: scanned, sorted, read. `pending` while the workers are still expected to be on
 * it; after a few minutes without a result it is shown as not read (no document types configured, or the
 * readers are down) and the screen stops checking.
 */
export function documentState(
  d: DocumentSummary,
  now = Date.now(),
): { label: string; tone: Tone; pending: boolean } {
  const waiting = now - Date.parse(d.created_at) < READING_MS;
  if (d.status === 'rejected')
    return {
      label: `Refused: the malware scan found ${d.scan.signature ?? 'something'}`,
      tone: 'bad',
      pending: false,
    };
  const c = d.classification;
  if (!c) return waiting ? { label: 'Reading…', tone: 'neutral', pending: true } : { label: 'Stored · not read', tone: 'neutral', pending: false }; // prettier-ignore
  if (c.status === 'failed') return { label: 'Couldn’t be read', tone: 'warn', pending: false };
  const flags = new Set(c.flags.map((f) => f.flag));
  if (flags.has('unreadable'))
    return { label: 'Couldn’t be read (a scan or image without text)', tone: 'warn', pending: false };
  if (flags.has('out_of_scope') || !c.document_type || c.document_type === 'other')
    return { label: 'Not recognised as evidence', tone: 'neutral', pending: false };
  const kind = typeLabel(c.document_type);
  const x = d.extraction;
  if (!x) return { label: waiting ? `${kind} · reading answers…` : kind, tone: 'neutral', pending: waiting };
  if (x.status !== 'extracted')
    return { label: `${kind} · answers couldn’t be read`, tone: 'warn', pending: false };
  const n = x.answers.length;
  return {
    label: `${kind} · ${n ? `${n} ${n === 1 ? 'answer' : 'answers'} found` : 'no answers found'}`,
    tone: 'ok',
    pending: false,
  };
}

/**
 * Open suggestions, in the order a person should see them: where a document disagrees with an answer or
 * with another document first, then answers for unanswered questions, then ones that only confirm.
 */
export function reviewGroups(list: readonly AnswerSuggestion[]) {
  const open = list.filter((s) => s.status === 'open');
  const conflicts = open.filter((s) => s.kind === 'conflict' || s.disagreeing.length > 0);
  // Sure, undisputed answers to optional questions are taken together in one click (task 106).
  const sure = together(open);
  const rest = open.filter((s) => !conflicts.includes(s) && !sure.includes(s));
  return {
    conflicts: byQuestion(conflicts),
    sure,
    fills: rest.filter((s) => s.kind === 'fill'),
    confirms: rest.filter((s) => s.kind === 'same'),
    total: open.length,
  };
}

/** Suggestions for the same question together, so disagreeing documents are decided side by side. */
function byQuestion(list: readonly AnswerSuggestion[]): AnswerSuggestion[][] {
  const groups = new Map<string, AnswerSuggestion[]>();
  for (const s of list) groups.set(s.question, [...(groups.get(s.question) ?? []), s]);
  return [...groups.values()];
}

/** A file question's answer: the documents attached, in order, without repeats. */
export function fileIds(value: unknown): string[] {
  const ids = (value as { document_ids?: unknown } | null | undefined)?.document_ids;
  return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
}

export function withFile(value: unknown, id: string, max: number): { document_ids: string[] } {
  return { document_ids: [...new Set([...fileIds(value), id])].slice(-max) };
}

export function withoutFile(value: unknown, id: string): { document_ids: string[] } | undefined {
  const ids = fileIds(value).filter((x) => x !== id);
  return ids.length ? { document_ids: ids } : undefined;
}
